import React, { useEffect, useMemo, useState } from "react";
import { Btn, Eyebrow } from "../../components/Primitives";

const SLOT_LABELS = {
    afternoon: "Afternoon (12-3pm)",
    early_eve: "Early evening (5-8pm)",
    evening: "Evening (7-10pm)",
};
const SLOTS = ["afternoon", "early_eve", "evening"];

function buildDays(daysAhead = 14) {
    const out = [];
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    for (let i = 1; i <= daysAhead; i++) {
        const d = new Date(base.getTime() + i * 86400000);
        const iso = d.toISOString().slice(0, 10);
        const day = d.toLocaleDateString(undefined, { weekday: "short" });
        const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        out.push({ iso, day, date });
    }
    return out;
}

function keyOf(w) { return `${w.date}|${w.slot}`; }

export default function AvailabilityPicker({ onSubmit, submitting, onCancel }) {
    const days = useMemo(() => buildDays(14), []);
    const [picked, setPicked] = useState([]); // array of {date, slot}

    const toggle = (date, slot) => {
        const w = { date, slot };
        const k = keyOf(w);
        if (picked.find(p => keyOf(p) === k)) {
            setPicked(picked.filter(p => keyOf(p) !== k));
        } else if (picked.length < 3) {
            setPicked([...picked, w]);
        }
    };

    const canSubmit = picked.length === 3 && !submitting;

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="availability-picker">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="picker-back-btn" onClick={onCancel} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">← Back</button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Step 1 of 3</span>
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>Pick your windows</Eyebrow>
                <h1 className="mt-2 font-display text-3xl text-burgundy leading-[1.15]">
                    <span className="italic">Three</span> times that work.
                </h1>
                <p className="mt-2 text-sm text-ink-muted leading-snug">
                    Pick exactly three windows in the next 14 days. Your match does the same. We'll surface the overlap.
                </p>
                <div className="mt-3 text-[10px] tracking-[0.32em] uppercase text-gold-deep" data-testid="picker-count">
                    {picked.length} / 3 selected
                </div>
            </div>

            <div className="flex-1 px-6 py-3 overflow-y-auto">
                <div className="space-y-3">
                    {days.map(d => (
                        <div key={d.iso} className="border border-burgundy/10 bg-white">
                            <div className="px-4 py-2 border-b border-burgundy/10 flex items-baseline justify-between">
                                <span className="font-display text-base text-burgundy">{d.day}, {d.date}</span>
                            </div>
                            <div className="grid grid-cols-3 divide-x divide-burgundy/10">
                                {SLOTS.map(slot => {
                                    const isPicked = picked.find(p => p.date === d.iso && p.slot === slot);
                                    const disabled = !isPicked && picked.length >= 3;
                                    return (
                                        <button
                                            key={slot}
                                            data-testid={`slot-${d.iso}-${slot}`}
                                            onClick={() => toggle(d.iso, slot)}
                                            disabled={disabled}
                                            className={`py-3 px-2 text-[10px] tracking-[0.18em] uppercase transition-colors ${
                                                isPicked
                                                    ? "bg-burgundy text-cream"
                                                    : disabled
                                                        ? "text-ink-light bg-cream-soft"
                                                        : "text-burgundy hover:bg-cream-soft"
                                            }`}
                                        >
                                            {SLOT_LABELS[slot].split(" ")[0]}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="px-6 pb-8 pt-3 border-t border-burgundy/10 bg-cream space-y-2">
                <Btn
                    data-testid="picker-submit"
                    variant="primary"
                    onClick={() => onSubmit(picked)}
                    disabled={!canSubmit}
                >
                    {submitting ? "Submitting…" : `Submit ${picked.length}/3 windows`}
                </Btn>
            </div>
        </div>
    );
}
